import { ILocation, INewShipmentBasicInfo } from '../interfaces';
import { IPindrop, IShipmentRecord } from '../../../api/shipment/interfaces';
import {
    IShipmentTemplate,
    ITemplateCreatePayload,
} from '../../../api/template/interfaces';
import { ICargoDetails } from '../../../components/CargoDetails/interfaces/ICargoDetails';
import { CARGO_ITEM } from '../../../components/CargoDetails/initialState';
import { ISelect } from '../../../utils/interfaces';
import { CommodityDropdownKey } from '../constants';
import {
    PER_BUSHEL,
    PER_CWT,
    PER_HEADCOUNT,
    PER_MILE,
    PER_TON,
    FLAT,
    NONE,
} from '../../../utils/constants/rateTypeDropdown';
import { standardizeString } from '../../../utils/functions';

export type AppliedTemplateShipmentParts = {
    newShipment: Partial<INewShipmentBasicInfo>;
    shipmentRecord: Partial<IShipmentRecord>;
    locations?: ILocation[];
};

export function mapPindropEnrichedLocation(
    templateLocation: ILocation | Partial<ILocation>,
    fallbackType: string,
    pindrops: IPindrop[],
    index: number,
    currentFirstLocationType?: string,
): ILocation {
    const locationType =
        templateLocation.locationType ??
        currentFirstLocationType ??
        (index === 0 ? 'Pickup' : 'DropOff');

    const latitude = Number(templateLocation.latitude ?? 0);
    const longitude = Number(templateLocation.longitude ?? 0);

    let streetAddress = templateLocation.streetAddress ?? '';
    let city = templateLocation.city ?? '';
    let state = templateLocation.state ?? '';
    let zipCode = templateLocation.zipCode ?? '';
    let country = templateLocation.country ?? '';
    let completeAddress = templateLocation.completeAddress ?? '';
    let instructions = templateLocation.instructions ?? '';
    let timeZone = templateLocation.timeZone ?? '';
    let latOut = latitude;
    let lngOut = longitude;
    const locationPindrop = templateLocation.pindrop?.pinDropId
        ? pindrops.find(
              pindrop =>
                  pindrop.pinDropId ===
                  Number(templateLocation.pindrop?.pinDropId),
          )
        : undefined;

    if ((!latOut || !lngOut) && locationPindrop?.location) {
        latOut = Number(locationPindrop.location.latitude || 0);
        lngOut = Number(locationPindrop.location.longitude || 0);
        streetAddress =
            streetAddress || locationPindrop.location.streetAddress || '';
        city = city || locationPindrop.location.city || '';
        state = state || locationPindrop.location.state || '';
        zipCode = zipCode || locationPindrop.location.zipCode || '';
        country = country || locationPindrop.location.country || '';
        timeZone = timeZone || locationPindrop.location.timeZone || '';
        if (!completeAddress) {
            completeAddress = `${
                locationPindrop.location.streetAddress ?? ''
            } ${locationPindrop.location.city ?? ''}, ${
                locationPindrop.location.state ?? ''
            }`.trim();
        }
        if (!instructions) {
            instructions = locationPindrop.location.instructions || '';
        }
    }

    return {
        locationId: templateLocation.locationId ?? 0,
        locationType: locationType ?? fallbackType,
        streetAddress,
        city,
        state,
        zipCode,
        country,
        latitude: latOut,
        longitude: lngOut,
        completeAddress,
        instructions,
        timeZone,
        pindrop: templateLocation.pindrop
            ? {
                  name: templateLocation.pindrop.name ?? '',
                  pinDropId: templateLocation.pindrop.pinDropId ?? '',
              }
            : { name: '', pinDropId: '' },
        shipmentLocationId: templateLocation.shipmentLocationId ?? null,
        ordinal: templateLocation.ordinal,
    };
}

export function mapTemplateToShipmentParts(
    template: IShipmentTemplate,
    context: { currentLocations: ILocation[]; pindrops: IPindrop[] },
): AppliedTemplateShipmentParts {
    const currentFirstLocationType = context.currentLocations[0]?.locationType;

    const result: AppliedTemplateShipmentParts = {
        newShipment: {},
        shipmentRecord: {},
    };

    const templateData: ITemplateCreatePayload | undefined =
        template.templateConfiguration;

    if (templateData) {
        if (templateData.contractNumber !== undefined) {
            result.newShipment.contractNumber =
                templateData.contractNumber ?? '';
        }
        if (templateData.estimatedDistance !== undefined) {
            result.newShipment.estimatedDistance =
                templateData.estimatedDistance ?? '';
        }

        if (
            Array.isArray(templateData.locations) &&
            templateData.locations.length > 0
        ) {
            result.locations = templateData.locations.map((location, index) =>
                mapPindropEnrichedLocation(
                    location,
                    index === 0 ? 'Pickup' : 'DropOff',
                    context.pindrops,
                    index,
                    currentFirstLocationType,
                ),
            );
        }

        const shipmentRecord: Partial<IShipmentRecord> = {};
        if (templateData.orderNotes !== undefined) {
            shipmentRecord.orderNotes = templateData.orderNotes;
        }
        if (templateData.driverInstructions !== undefined) {
            shipmentRecord.driverInstructions = templateData.driverInstructions;
        }
        if (templateData.time !== undefined) {
            shipmentRecord.loadTime = templateData.time;
        }
        if (templateData.deliveryStartDate !== undefined) {
            shipmentRecord.deliveryStartDate =
                templateData.deliveryStartDate as string | Date;
        }
        if (templateData.deliveryEndDate !== undefined) {
            shipmentRecord.deliveryEndDate = templateData.deliveryEndDate as
                | string
                | Date;
        }
        if (templateData.isCattlePORequest !== undefined) {
            shipmentRecord.isCattlePORequest = templateData.isCattlePORequest;
        }
        if (
            templateData.shrinkPercentage !== undefined &&
            templateData.shrinkPercentage !== null
        ) {
            shipmentRecord.shrinkPercentage = Number(
                templateData.shrinkPercentage,
            );
        }
        if (templateData.loads !== undefined && templateData.loads !== null) {
            shipmentRecord.loadNumber = Number(templateData.loads);
        }
        if (
            templateData._49kTrucksRequired !== undefined &&
            templateData._49kTrucksRequired !== null
        ) {
            shipmentRecord._49kTrucksRequired = Number(
                templateData._49kTrucksRequired,
            );
        }
        if (
            templateData._52kTrucksRequired !== undefined &&
            templateData._52kTrucksRequired !== null
        ) {
            shipmentRecord._52kTrucksRequired = Number(
                templateData._52kTrucksRequired,
            );
        }
        if (
            templateData._55_62kTrucksRequired !== undefined &&
            templateData._55_62kTrucksRequired !== null
        ) {
            shipmentRecord._55_62kTrucksRequired = Number(
                templateData._55_62kTrucksRequired,
            );
        }
        if (
            templateData._68kTrucksRequired !== undefined &&
            templateData._68kTrucksRequired !== null
        ) {
            shipmentRecord._68kTrucksRequired = Number(
                templateData._68kTrucksRequired,
            );
        }
        result.shipmentRecord = shipmentRecord;
    } else {
        result.newShipment.contractNumber = template.contractNumber || '';
        result.newShipment.estimatedDistance = template.estimatedDistance || '';
        if (
            Array.isArray(template.locations) &&
            template.locations.length > 0
        ) {
            result.locations = template.locations;
        }
        result.shipmentRecord = {
            ...(template.shipmentRecord ?? {}),
        } as Partial<IShipmentRecord>;
    }

    return result;
}

function normalizeTemplateRateType(rateTypeRaw: string | undefined): {
    value: string;
    label: string;
} {
    const rateType = (rateTypeRaw || '').trim().toLowerCase();
    switch (rateType) {
        case 'per mile':
        case 'permile':
            return { value: PER_MILE, label: 'Per Mile' };
        case 'per cwt':
        case 'percwt':
            return { value: PER_CWT, label: 'Per CWT' };
        case 'flat':
            return { value: FLAT, label: 'Flat' };
        case 'per ton':
        case 'perton':
            return { value: PER_TON, label: 'Per Ton' };
        case 'per bushel':
        case 'perbushel':
            return { value: PER_BUSHEL, label: 'Per Bushel' };
        case 'per headcount':
        case 'per head count':
        case 'perheadcount':
        case 'per quantity':
        case 'perquantity':
            return { value: PER_HEADCOUNT, label: 'Per Head Count' };
        default:
            return { value: NONE, label: 'Select Rate Type' };
    }
}

export function mapTemplateToCargoDetails(
    template: IShipmentTemplate,
    currentLocations?: ILocation[],
    commodityOptions?: ISelect[],
): ICargoDetails[] {
    const templateData = template.templateConfiguration;
    if (
        !templateData ||
        !Array.isArray(templateData.cargoDetails) ||
        templateData.cargoDetails.length === 0
    ) {
        return [];
    }

    const normaliseString = (inputValue: string | ''): string =>
        standardizeString(inputValue).replace(/\s+/g, ' ');

    type TemplateCargoLoc = {
        locationType?: string;
        city?: string;
        state?: string;
        streetAddress?: string;
        zipCode?: string;
        country?: string;
        completeAddress?: string;
        timeZone?: string;
        pindrop?: { name: string; pinDropId: string | number };
        estHeadCount?: number | null;
        estWeightInPounds?: number | null;
        estMiles?: number | null;
    };

    const addressKey = (location: TemplateCargoLoc, withZip = true): string => {
        const parts = [
            normaliseString(location.streetAddress ?? ''),
            normaliseString(location.city ?? ''),
            normaliseString(location.state ?? ''),
        ];
        if (withZip) parts.push(normaliseString(location.zipCode ?? ''));
        return parts.filter(Boolean).join('|');
    };

    const hasRealShipmentLocationIds = Array.isArray(currentLocations)
        ? currentLocations.some(
              l =>
                  l.shipmentLocationId !== null &&
                  l.shipmentLocationId !== undefined,
          )
        : false;

    const byPindrop = new Map<string, ILocation>();
    const byAddress = new Map<string, ILocation>();

    if (hasRealShipmentLocationIds && Array.isArray(currentLocations)) {
        currentLocations.forEach(l => {
            if (
                l.shipmentLocationId === null ||
                l.shipmentLocationId === undefined
            ) {
                return;
            }
            if (
                l.pindrop?.pinDropId !== undefined &&
                l.pindrop?.pinDropId !== null &&
                l.pindrop?.pinDropId !== ''
            ) {
                byPindrop.set(String(l.pindrop.pinDropId), l);
            }
            const locLike: TemplateCargoLoc = {
                streetAddress: (l as ILocation).streetAddress,
                city: (l as ILocation).city,
                state: (l as ILocation).state,
                zipCode: (l as ILocation).zipCode,
            };
            const keyFull = addressKey(locLike, true);
            const keyZipless = addressKey(locLike, false);
            if (keyFull) byAddress.set(keyFull, l);
            if (keyZipless) byAddress.set(keyZipless, l);
        });
    }

    const matchTemplateLocToShipment = (
        templateCargoLocation: TemplateCargoLoc,
    ): ILocation | undefined => {
        if (!hasRealShipmentLocationIds) return undefined;

        if (templateCargoLocation?.pindrop?.pinDropId) {
            const matchedLocation = byPindrop.get(
                String(templateCargoLocation.pindrop.pinDropId),
            );
            if (
                matchedLocation &&
                (templateCargoLocation.locationType
                    ? normaliseString(matchedLocation.locationType) ===
                      normaliseString(templateCargoLocation.locationType)
                    : true)
            ) {
                return matchedLocation;
            }
        }

        const key = addressKey(templateCargoLocation);
        if (key) {
            const matchedLocation = byAddress.get(key);
            if (
                matchedLocation &&
                (templateCargoLocation.locationType
                    ? normaliseString(matchedLocation.locationType) ===
                      normaliseString(templateCargoLocation.locationType)
                    : true)
            ) {
                return matchedLocation;
            }
        }
        return undefined;
    };

    const isValidCommodityOption = (commodityOption: ISelect): boolean =>
        !!commodityOption?.value &&
        commodityOption.value !== '0' &&
        commodityOption.value !== CommodityDropdownKey;
    const findCommodityByName = (name?: string): ISelect | undefined => {
        if (!commodityOptions || !name) return undefined;
        const key = normaliseString(name);
        return commodityOptions.find(
            commodityOption =>
                isValidCommodityOption(commodityOption) &&
                normaliseString(commodityOption.label) === key,
        );
    };
    const findCommodityById = (id?: number | string): ISelect | undefined => {
        if (!commodityOptions || id === undefined || id === null)
            return undefined;
        const idStr = String(id);
        return commodityOptions.find(
            commodityOption =>
                isValidCommodityOption(commodityOption) &&
                String(commodityOption.value) === idStr,
        );
    };

    return templateData.cargoDetails.map(cargoDetail => {
        const normalizedRate = normalizeTemplateRateType(cargoDetail.rateType);
        const resolvedCargoById = findCommodityById(
            (cargoDetail as { commodityId?: number | string }).commodityId,
        );
        const resolvedCargoByName = findCommodityByName(
            cargoDetail.commodityType,
        );
        const matchedCommodity = resolvedCargoById || resolvedCargoByName;

        let cargoPickups: { shipmentLocationId: number }[] = [];
        let cargoDropOffs: { shipmentLocationId: number }[] = [];

        type TemplateCargoDetail = NonNullable<
            ITemplateCreatePayload['cargoDetails']
        >[number];
        type WithCargoLocations = TemplateCargoDetail & {
            cargoLocations?: TemplateCargoLoc[];
        };

        const hasCargoLocations = (
            templateCargoDetail: TemplateCargoDetail | WithCargoLocations,
        ): templateCargoDetail is WithCargoLocations =>
            'cargoLocations' in templateCargoDetail &&
            Array.isArray(templateCargoDetail.cargoLocations);

        const templateCargoLocs: TemplateCargoLoc[] =
            Array.isArray((cargoDetail as TemplateCargoDetail).locations) &&
            (cargoDetail as TemplateCargoDetail).locations.length > 0
                ? ((cargoDetail as TemplateCargoDetail)
                      .locations as TemplateCargoLoc[])
                : hasCargoLocations(
                      cargoDetail as TemplateCargoDetail | WithCargoLocations,
                  ) &&
                  Array.isArray(
                      (cargoDetail as WithCargoLocations).cargoLocations,
                  )
                ? ((cargoDetail as WithCargoLocations)
                      .cargoLocations as TemplateCargoLoc[])
                : [];

        if (hasRealShipmentLocationIds && templateCargoLocs.length > 0) {
            const pickups: { shipmentLocationId: number }[] = [];
            const dropoffs: { shipmentLocationId: number }[] = [];

            templateCargoLocs.forEach(location => {
                const matchedCargoLocation =
                    matchTemplateLocToShipment(location);
                const shipmentLocationId =
                    matchedCargoLocation?.shipmentLocationId;
                if (
                    matchedCargoLocation &&
                    shipmentLocationId !== null &&
                    shipmentLocationId !== undefined
                ) {
                    const type = normaliseString(
                        (location.locationType || '').replace(/\s+/g, ''),
                    );
                    const base = {
                        shipmentLocationId,
                        estHeadCount:
                            location.estHeadCount !== undefined &&
                            location.estHeadCount !== null
                                ? Number(location.estHeadCount)
                                : null,
                        estWeightInPounds:
                            location.estWeightInPounds !== undefined &&
                            location.estWeightInPounds !== null
                                ? Number(location.estWeightInPounds)
                                : null,
                        estMiles:
                            location.estMiles !== undefined &&
                            location.estMiles !== null
                                ? Number(location.estMiles)
                                : null,
                    };
                    if (type === 'pickup') {
                        pickups.push(base);
                    } else if (type === 'dropoff') {
                        dropoffs.push(base);
                    }
                }
            });

            cargoPickups = pickups;
            cargoDropOffs = dropoffs;
        }

        return {
            ...CARGO_ITEM,
            selectedCommodity: {
                value: String(
                    (cargoDetail as { commodityId?: string | number })
                        .commodityId ??
                        matchedCommodity?.value ??
                        '',
                ),
                label:
                    matchedCommodity?.label || cargoDetail.commodityType || '',
            },
            rateType: {
                value: normalizedRate.value,
                label: normalizedRate.label,
            },
            rateInUSD: String(cargoDetail.rateInUSD ?? ''),
            prorateRate: String(cargoDetail.prorateRate ?? ''),
            estWeightInPounds: cargoDetail.estWeightInPounds ?? null,
            estHeadCount: cargoDetail.estHeadCount ?? null,
            estMiles: cargoDetail.estMiles ?? null,
            producer: cargoDetail.producer || null,
            cargoPickups,
            cargoDropOffs,
            shipmentCargoLocations: [...cargoPickups, ...cargoDropOffs],
            driverLoadCargoLocations: [],
            isCargoAccordionActive: false,
            isAddAllLocationToCommodity: false,
        };
    });
}
